import { useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import { Button, type CustomMaskProps, HStack, Text, useCustomMask, VStack } from '@direct-frontend/components';
import { waitForSelector } from '@direct-frontend/modules-store';
import { type Nullable } from '@direct-frontend/stdlib';
import { useField, useFormInstance } from '@adv-frontend/form-kit';
import {
    getLoadingOrder,
    parseConfirmVerificationErrorForOrder,
    parseStartVerificationErrorForOrder,
    useConfirmPhoneVerificationForOrder,
    useStartPhoneVerificationForOrder,
} from '@/entities/order';
import { MaskedTextInput } from '@/shared/components/TextInput';
import { formatPhoneNumber } from '@/shared/lib/formatPhoneNumber';
import { makeModifierClassname } from '@/shared/lib/makeModifierClassname';
import { useSecondsCountdown } from '../../lib/useSecondsCountdown';
import { clientForm } from '../../model/clientForm';
import { CLIENT_FORM_ID } from '../../model/constants';
import { type ClientFormProps } from '../../types';
import styles from './ClientFormPhoneVerification.module.css';

const textInputModifier = makeModifierClassname(styles, 'textInput');

export const ClientFormPhoneVerification = (props: ClientFormProps) => {
    const { onClose, onSubmit: propOnSubmit } = props;
    const { fields, submit } = useFormInstance({ form: clientForm, id: CLIENT_FORM_ID });
    const { value: phone } = useField(fields.phone);

    const [codeInput, setCodeInput] = useState({ errorMessage: ' ', value: '' });
    const onInputChange = useCallback((value: string) => setCodeInput({ errorMessage: ' ', value }), []);

    const { isFetching, refetch, data, error } = useStartPhoneVerificationForOrder({ phone });
    const [trigger, { isLoading }] = useConfirmPhoneVerificationForOrder();
    const startVerificationError = Boolean(error) && parseStartVerificationErrorForOrder(error);
    const countdown = useSecondsCountdown(data?.resend_available_at);

    const codeMaskProps = useMemo<CustomMaskProps>(() => {
        return {
            mask: new Array(data?.code_length ?? 10).fill(/\d/),
            transformToUnmasked: (value: string) => value.trim(),
        };
    }, [data?.code_length]);

    const codeMask = useCustomMask(codeMaskProps);

    const onSubmit = useCallback(async () => {
        let errorMessage: Nullable<string> = null;

        if (data) {
            if (codeInput.value.length !== data.code_length) {
                errorMessage = `Код должен состоять из ${data.code_length} цифр`;
            }
        } else {
            errorMessage = 'Код не был запрошен';
        }

        if (!errorMessage) {
            try {
                await trigger({ code: codeInput.value }).unwrap();
            } catch (error) {
                errorMessage = parseConfirmVerificationErrorForOrder(error);
            }
        }

        if (errorMessage) {
            return setCodeInput((prev) => {
                return { errorMessage, value: prev.value };
            });
        }

        fields.phoneVerified.setValue(phone);

        const values = await submit();

        if (values) {
            propOnSubmit(values);
            onClose();

            // update snapshot триггерится после обновления формы, ждём завершения
            await waitForSelector(() => !getLoadingOrder().isOrderUpdating);
        }
    }, [data, fields.phoneVerified, phone, submit, codeInput.value, trigger, propOnSubmit, onClose]);

    return (
        <VStack className={styles.root}>
            <Text
                className={cn(textInputModifier('numbers', true), textInputModifier('paddingTop6', true))}
                color={startVerificationError ? 'red' : 'current'}
                size="mc"
            >
                {startVerificationError || `Код отправлен на номер ${formatPhoneNumber(phone)}`}
            </Text>

            <VStack gap="4">
                <MaskedTextInput
                    isDisabled={Boolean(startVerificationError)}
                    label="Введите код из sms *"
                    maskConfig={codeMask}
                    onChange={onInputChange}
                    value={codeInput.value}
                />
                <Text color="red">{codeInput.errorMessage}</Text>
            </VStack>

            <VStack gap="16" className={styles.buttons}>
                <Button
                    color="normal"
                    isDisabled={!countdown || countdown.num > 0}
                    isLoading={isFetching}
                    onClick={refetch}
                    size="m"
                >
                    {countdown?.num ? (
                        <HStack justifyContent="space-between">
                            <Text color="gray-disabled">Запросить код повторно</Text>
                            <Text className={textInputModifier('numbers', true)}>Через {countdown.formatted}</Text>
                        </HStack>
                    ) : (
                        'Запросить код повторно'
                    )}
                </Button>
                {startVerificationError ? (
                    <Button color="accent" isLoading={isLoading} onClick={onClose} size="m">
                        Закрыть
                    </Button>
                ) : (
                    <Button color="accent" isLoading={isLoading} onClick={onSubmit} size="m">
                        Подтвердить номер
                    </Button>
                )}
            </VStack>
        </VStack>
    );
};
